/**
 * disciplineTelemetry — INTEL-AEGIS-CA-8
 *
 * Per-agent intervention telemetry for the disciplineEnforcer.
 * Records every enforcer verdict (which gate tripped, return type, agentId)
 * to Firestore for operator visibility at /operator/discipline-metrics.
 *
 * Architecture:
 *   - Zero synchronous overhead on the enforcer code path (fire-and-forget)
 *   - Per-agent counters buffered in memory; flushed to Firestore every FLUSH_INTERVAL_MS
 *   - Single Firestore document per (tenantId, agentId, dateKey) aggregation bucket
 *   - Collection: `discipline_telemetry/{tenantId}/events/{id}` (raw)
 *   - Collection: `discipline_telemetry_agg/{tenantId}/{agentId}_{dateKey}` (aggregated)
 *
 * KTRUST-L1 resilience: telemetry failure never blocks the enforcer verdict.
 */

import DataService from '../DataService.js';

const FLUSH_INTERVAL_MS = 30_000; // flush in-memory counters every 30s
const EVENTS_COLL = 'discipline_telemetry';
const AGG_COLL = 'discipline_telemetry_agg';

// In-memory accumulator: `${tenantId}:${agentId}:${dateKey}` → counters
const _buffer = new Map();
let _flushTimer = null;

/**
 * Record a single enforcer verdict. Fire-and-forget.
 *
 * @param {object} opts
 * @param {string} opts.tenantId
 * @param {string} opts.agentId
 * @param {string} opts.module
 * @param {string} opts.decisionType
 * @param {string} opts.returnType       - 'stated_position' | 'abstention' | 'silence'
 * @param {string|null} opts.trippedGate
 * @param {string} opts.enforcerVersion
 */
export function recordVerdict({
  tenantId,
  agentId,
  module,
  decisionType,
  returnType,
  trippedGate,
  enforcerVersion,
}) {
  if (!tenantId || !agentId) return;

  const dateKey = new Date().toISOString().slice(0, 10);
  const bufKey = `${tenantId}:${agentId}:${dateKey}`;

  if (!_buffer.has(bufKey)) {
    _buffer.set(bufKey, {
      tenantId, agentId, dateKey,
      total: 0, stated: 0, abstained: 0, silenced: 0,
      gateTrips: {}, // gate name → count
    });
  }

  const b = _buffer.get(bufKey);
  b.total++;
  if (returnType === 'stated_position') b.stated++;
  if (returnType === 'abstention') b.abstained++;
  if (returnType === 'silence') b.silenced++;
  if (trippedGate) {
    b.gateTrips[trippedGate] = (b.gateTrips[trippedGate] || 0) + 1;
  }

  _scheduleFlush();

  // Raw event — written async; never awaited
  _writeRawEvent({ tenantId, agentId, module, decisionType, returnType, trippedGate, enforcerVersion, dateKey }).catch(() => {});
}

async function _writeRawEvent(data) {
  try {
    await DataService.create(
      `${EVENTS_COLL}/${data.tenantId}/events`,
      {
        ...data,
        recordedAt: new Date().toISOString(),
        softDeleted: false,
        tenantId: data.tenantId,
      },
      'discipline_telemetry',
      data.tenantId,
    );
  } catch {
    // Non-fatal; raw events may be dropped
  }
}

function _scheduleFlush() {
  if (_flushTimer) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    _flush().catch(() => {});
  }, FLUSH_INTERVAL_MS);
}

async function _flush() {
  if (_buffer.size === 0) return;
  const entries = [..._buffer.entries()];
  _buffer.clear();

  await Promise.allSettled(entries.map(([, counters]) => _flushCounters(counters)));
}

async function _flushCounters(counters) {
  const { tenantId, agentId, dateKey } = counters;
  const docId = `${agentId}_${dateKey}`;

  try {
    const existing = await DataService.getById(`${AGG_COLL}/${tenantId}`, docId, tenantId);
    const merged = existing
      ? {
          ...existing,
          total: (existing.total || 0) + counters.total,
          stated: (existing.stated || 0) + counters.stated,
          abstained: (existing.abstained || 0) + counters.abstained,
          silenced: (existing.silenced || 0) + counters.silenced,
          gateTrips: _mergeGateTrips(existing.gateTrips || {}, counters.gateTrips),
          lastUpdatedAt: new Date().toISOString(),
        }
      : {
          tenantId, agentId, dateKey,
          ...counters,
          createdAt: new Date().toISOString(),
          lastUpdatedAt: new Date().toISOString(),
          softDeleted: false,
        };

    await DataService.set(`${AGG_COLL}/${tenantId}`, docId, merged, tenantId);
  } catch {
    // Non-fatal
  }
}

function _mergeGateTrips(existing, incoming) {
  const merged = { ...existing };
  for (const [gate, count] of Object.entries(incoming)) {
    merged[gate] = (merged[gate] || 0) + count;
  }
  return merged;
}

/**
 * Manually flush the in-memory buffer (for tests / graceful shutdown).
 */
export async function flushTelemetry() {
  clearTimeout(_flushTimer);
  _flushTimer = null;
  await _flush();
}

/**
 * Get aggregated telemetry for an agent.
 * @param {string} tenantId
 * @param {string} agentId
 * @param {string} [dateKey]  - YYYY-MM-DD; defaults to today
 */
export async function getAgentMetrics(tenantId, agentId, dateKey) {
  const key = dateKey || new Date().toISOString().slice(0, 10);
  const docId = `${agentId}_${key}`;
  return DataService.getById(`${AGG_COLL}/${tenantId}`, docId, tenantId);
}
